interface HubSpotForm {
  forms: {
    create: (opts: Record<string, unknown>) => void;
  };
}

declare global {
  interface Window {
    hbspt?: HubSpotForm;
  }
}

const STORAGE_PREFIX = 'interact:video-demo-unlocked:';

/** Query parameter that deep-links into a modal, matched against its campaign key. */
const OPEN_PARAM = 'open';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// page_title (not page_name) to match every other tracking listener, so these
// events join to the global book_a_demo_submit / form_submission on one key.
const trackPageName = (): string => window.InteractTracking?.getContext().page_title ?? '';

function initModal(wrap: HTMLElement): void {
  const portalId = wrap.dataset.portalId ?? '';
  const formId = wrap.dataset.formId ?? '';
  const region = wrap.dataset.region ?? 'na1';
  const sfdcCampaignId = wrap.dataset.sfdcCampaignId ?? '';
  const triggerAnchor = wrap.dataset.triggerAnchor ?? '';
  const openParam = wrap.dataset.openParam || triggerAnchor;
  const redirectUrl = wrap.dataset.redirectUrl ?? '';
  const unlockKey = wrap.dataset.unlockKey ?? '';

  if (!portalId || !formId || !triggerAnchor) {
    return;
  }

  const panel = wrap.querySelector<HTMLElement>('.interact-cta-modal__panel');
  const formTarget = wrap.querySelector<HTMLElement>('.interact-cta-modal__form');
  if (!panel || !formTarget) {
    return;
  }

  let formInitialised = false;
  let opened = false;
  let lastFocusedElement: HTMLElement | null = null;
  let modalEngaged = false;
  let modalFormFocused = false;
  let modalSubmitted = false;
  let revealed = false;
  let pendingObserver: MutationObserver | null = null;

  const initHubSpotForm = (): void => {
    if (formInitialised) {
      return;
    }
    const opts: Record<string, unknown> = {
      portalId,
      formId,
      region,
      target: `#${formTarget.id}`,
    };
    if (sfdcCampaignId) {
      opts.sfdcCampaignId = sfdcCampaignId;
    }
    if (window.hbspt && window.hbspt.forms && typeof window.hbspt.forms.create === 'function') {
      window.hbspt.forms.create(opts);
      formInitialised = true;
    }
  };

  // Mirrors the probe in inc/hubspot-lazy.php — HubSpot renders inline or in an iframe.
  const MOUNTED_SELECTOR = '.hs-form, .hbspt-form, iframe, .submitted-message, .hs-form-iframe';

  const formMounted = (): boolean => !!formTarget.querySelector(MOUNTED_SELECTOR);

  // Revealing the panel at its final height is what stops it resizing under the
  // visitor; the wait is capped so an ad-blocked HubSpot still opens the modal.
  const whenFormMounted = (done: (mounted: boolean) => void): void => {
    if (formMounted()) {
      done(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (!formMounted()) return;
      observer.disconnect();
      clearTimeout(timer);
      done(true);
    });
    observer.observe(formTarget, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      done(false);
    }, 400);
  };

  // Slow-network path only: drop the reserved height once the form finally lands.
  const whenLateFormMounted = (): void => {
    pendingObserver?.disconnect();
    pendingObserver = new MutationObserver(() => {
      if (!formMounted()) return;
      pendingObserver?.disconnect();
      pendingObserver = null;
      wrap.classList.remove('is-form-pending');
    });
    pendingObserver.observe(formTarget, { childList: true, subtree: true });
  };

  const open = (): void => {
    if (opened) return;
    opened = true;

    initHubSpotForm();

    lastFocusedElement = document.activeElement as HTMLElement | null;

    whenFormMounted((mounted) => {
      if (!opened) return;
      revealed = true;

      window.InteractTracking?.track('cta_modal_shown', {
        page_name: trackPageName(),
      });

      wrap.classList.toggle('is-form-pending', !mounted);
      wrap.removeAttribute('hidden');
      wrap.setAttribute('aria-hidden', 'false');
      document.body.classList.add('interact-cta-modal-open');
      // Defer .is-open by a frame so the CSS transition catches the change.
      requestAnimationFrame(() => {
        wrap.classList.add('is-open');
        panel.focus();
      });

      if (!mounted) {
        whenLateFormMounted();
      }
    });
  };

  const close = (method: 'close_button' | 'backdrop' | 'escape'): void => {
    if (!opened) return;
    opened = false;

    pendingObserver?.disconnect();
    pendingObserver = null;

    // Closed while the form was still mounting, so nothing was ever on screen.
    if (!revealed) return;
    revealed = false;

    // A converted session already emitted cta_modal_submit; closing the
    // thank-you screen is not an abandonment, so don't also count a dismissal.
    if (!modalSubmitted) {
      window.InteractTracking?.track('cta_modal_dismissed', {
        page_name: trackPageName(),
        method,
        engaged: modalEngaged,
      });
    }

    wrap.classList.remove('is-open');
    wrap.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('interact-cta-modal-open');

    // Match the 0.3s fade-out before re-hiding from assistive tech.
    setTimeout(() => {
      if (!opened) {
        wrap.setAttribute('hidden', '');
      }
    }, 350);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  };

  const samePath = (a: string, b: string): boolean =>
    a.replace(/\/+$/, '') === b.replace(/\/+$/, '');

  // Any same-page link to #<anchor> is a trigger, so editors wire the button by URL
  // alone — the link UI stores that as "#a", "/page/#a" or the full permalink.
  const isTrigger = (el: HTMLElement): boolean => {
    if (el.dataset.ctaModalOpen === triggerAnchor) return true;

    const href = el.getAttribute('href');
    if (!href) return false;

    try {
      const url = new URL(href, window.location.href);
      return (
        url.hash === `#${triggerAnchor}` &&
        url.origin === window.location.origin &&
        samePath(url.pathname, window.location.pathname)
      );
    } catch {
      return false;
    }
  };

  const triggerFrom = (e: Event): HTMLElement | null => {
    const target = e.target as HTMLElement | null;
    const trigger = target?.closest<HTMLElement>('a[href], [data-cta-modal-open]');
    return trigger && isTrigger(trigger) ? trigger : null;
  };

  document.addEventListener('click', (e) => {
    if (!triggerFrom(e)) return;
    e.preventDefault();
    open();
  });

  // Only `open` is stripped afterwards; utm_* and gclid stay for inc/attribution.php.
  const openFromUrl = (): void => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get(OPEN_PARAM) !== openParam) return;

      params.delete(OPEN_PARAM);
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
      );

      open();
    } catch {
      /* deep-linking is optional; never block the normal button behaviour */
    }
  };

  openFromUrl();

  // Mounting on hover/focus buys HubSpot the time it needs, so the click opens
  // a panel that is already its final size.
  const preload = (e: Event): void => {
    if (triggerFrom(e)) initHubSpotForm();
  };
  document.addEventListener('pointerover', preload);
  document.addEventListener('focusin', preload);
  document.addEventListener('touchstart', preload, { passive: true });

  wrap.querySelectorAll<HTMLElement>('[data-cta-modal-close]').forEach((el) => {
    const method = el.classList.contains('interact-cta-modal__backdrop')
      ? 'backdrop'
      : 'close_button';
    el.addEventListener('click', () => close(method));
  });

  document.addEventListener('keydown', (e) => {
    if (!opened) return;
    if (e.key === 'Escape') {
      close('escape');
    } else if (e.key === 'Tab') {
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      // While HubSpot is still mounting there's only the close button; pin Tab to it.
      if (focusables.length === 1) {
        e.preventDefault();
        focusables[0].focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Any focus inside the modal's own form scopes conversion attribution to it —
  // covers typing and pre-filled one-click submits by known HubSpot contacts.
  formTarget.addEventListener('focusin', () => {
    modalFormFocused = true;
  });

  formTarget.addEventListener('input', (e) => {
    if (modalEngaged || !opened || !e.isTrusted) return;
    modalEngaged = true;
    window.InteractTracking?.track('cta_modal_engaged', {
      page_name: trackPageName(),
    });
  });

  // Two copies of one form on a page are indistinguishable by form GUID alone, so
  // fall back to "was this modal's form the one touched" only in that case —
  // requiring focus always would strand Safari, which doesn't focus a clicked button.
  const formIsAmbiguous = (): boolean =>
    document.querySelectorAll(`[data-form-id="${CSS.escape(formId)}"]`).length > 1;

  // HubSpot's submit message carries no DOM origin, so scope it by form GUID and
  // only count it while this modal is the one on screen.
  const onFormSubmitted = (e: MessageEvent): void => {
    if (modalSubmitted || !opened) return;
    if (formIsAmbiguous() && !modalFormFocused) return;
    const data = e.data as {
      type?: string;
      eventName?: string;
      id?: string;
      data?: { formGuid?: string };
    } | null;
    if (!data || data.type !== 'hsFormCallback' || data.eventName !== 'onFormSubmitted') return;
    if ((data.id || data.data?.formGuid || '') !== formId) return;
    modalSubmitted = true;

    window.InteractTracking?.track('cta_modal_submit', {
      page_name: trackPageName(),
      form_id: formId,
    });

    if (unlockKey) {
      try {
        localStorage.setItem(`${STORAGE_PREFIX}${unlockKey}`, '1');
      } catch {
        // Storage blocked (private mode) — the ?unlocked= handoff on the redirect still opens the gate.
      }
    }

    if (redirectUrl) {
      // Give the dataLayer push above a tick to leave before the page unloads.
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 150);
    }
  };
  window.addEventListener('message', onFormSubmitted);

  window.addEventListener(
    'pagehide',
    () => {
      window.removeEventListener('message', onFormSubmitted);
    },
    { once: true },
  );
}

function bootstrap(): void {
  document.querySelectorAll<HTMLElement>('.interact-cta-modal').forEach(initModal);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}

export {};
